import { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { FileSpreadsheet, FileText, Upload, X } from 'lucide-react';
import { Button, Modal } from '../ui';
import simulationService from '../../services/simulationService';
import { showError, showSuccess } from '../../utils/toast';

/**
 * Import a simulation a trainer already owns as Word + Excel.
 *
 * No AI is involved: the parse is deterministic, so what comes out is exactly what the
 * trainer wrote. That matters for trust — the first thing they see the product do with
 * their material is reproduce it faithfully, not reinterpret it.
 */
const ImportSimulationModal = ({ open, onClose, onImported }) => {
  const docxRef = useRef(null);
  const xlsxRef = useRef(null);
  const [docx, setDocx] = useState(null);
  const [xlsx, setXlsx] = useState(null);
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);

  const reset = () => { setDocx(null); setXlsx(null); setTitle(''); setBusy(false); };

  const submit = async () => {
    if (!docx && !xlsx) {
      showError('Attach at least one file');
      return;
    }
    setBusy(true);
    try {
      const res = await simulationService.importFiles({ docx, xlsx, title: title.trim() || undefined });
      const n = res.conflicts.length;
      showSuccess(
        n
          ? `Imported — ${n} thing${n > 1 ? 's' : ''} in your files disagreed, flagged for review`
          : 'Imported'
      );
      reset();
      onImported?.(res.simulation);
    } catch (err) {
      showError(err.userMessage || err.message || 'Could not read those files');
      setBusy(false);
    }
  };

  const FilePicker = ({ label, hint, file, inputRef, accept, onPick, Icon }) => (
    <div>
      <span className="text-xs font-semibold uppercase tracking-wide text-neutral-500">{label}</span>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => onPick(e.target.files?.[0] || null)}
      />
      {file ? (
        <div className="mt-1.5 flex items-center gap-2.5 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2.5 dark:border-emerald-500/20 dark:bg-emerald-500/10">
          <Icon size={16} strokeWidth={1.75} className="shrink-0 text-emerald-600 dark:text-emerald-400" />
          <span className="min-w-0 flex-1 truncate text-sm text-neutral-800 dark:text-neutral-100">{file.name}</span>
          <button
            type="button"
            onClick={() => onPick(null)}
            className="shrink-0 rounded-lg p-1 text-neutral-400 hover:bg-white/60 hover:text-red-600"
            aria-label={`Remove ${file.name}`}
          >
            <X size={14} strokeWidth={2} />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="mt-1.5 flex w-full items-center gap-2.5 rounded-xl border-2 border-dashed border-neutral-300 px-3 py-3 text-left transition-colors hover:border-neutral-400 dark:border-neutral-700 dark:hover:border-neutral-600"
        >
          <Icon size={16} strokeWidth={1.75} className="shrink-0 text-neutral-400" />
          <span className="text-sm text-neutral-500 dark:text-neutral-400">{hint}</span>
        </button>
      )}
    </div>
  );

  FilePicker.propTypes = {
    label: PropTypes.string, hint: PropTypes.string, file: PropTypes.object,
    inputRef: PropTypes.object, accept: PropTypes.string, onPick: PropTypes.func, Icon: PropTypes.elementType,
  };

  // Modal calls onClose() directly on Escape, so this must stay callable —
  // a no-op while busy, never undefined.
  return (
    <Modal
      isOpen={open}
      onClose={busy ? () => {} : onClose}
      title="Import a simulation you already have"
      size="md"
    >
      <div className="space-y-4">
        <p className="text-sm leading-relaxed text-neutral-600 dark:text-neutral-300">
          If you already run this workshop from a Word file of scenarios and an Excel file
          of decisions, upload them. They&apos;re parsed exactly as written — nothing is
          rewritten or invented.
        </p>

        <FilePicker
          label="Scenarios"
          hint="Choose a Word file (.docx)"
          file={docx}
          inputRef={docxRef}
          accept=".docx"
          onPick={setDocx}
          Icon={FileText}
        />

        <FilePicker
          label="Decisions & scoring"
          hint="Choose an Excel file (.xlsx)"
          file={xlsx}
          inputRef={xlsxRef}
          accept=".xlsx"
          onPick={setXlsx}
          Icon={FileSpreadsheet}
        />

        <div>
          <span className="text-xs font-semibold uppercase tracking-wide text-neutral-500">Name it (optional)</span>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Run or Repair?"
            className="mt-1.5 w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm text-neutral-800 placeholder:text-neutral-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-100"
          />
        </div>

        <p className="rounded-lg bg-neutral-50 px-3 py-2 text-[11px] leading-relaxed text-neutral-500 dark:bg-neutral-800/60 dark:text-neutral-400">
          The Excel file needs a <span className="font-medium">Decisions Sheet</span> tab —
          it&apos;s the only one carrying the &ldquo;Leadership Angle&rdquo; column, which is
          what the debrief is built on. Anything the two files disagree about is flagged for
          you rather than quietly resolved.
        </p>

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="primary-filled" onClick={submit} loading={busy} disabled={busy || (!docx && !xlsx)}>
            <Upload size={15} strokeWidth={2} className="mr-1.5" />
            {busy ? 'Reading…' : 'Import'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

ImportSimulationModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onImported: PropTypes.func,
};

export default ImportSimulationModal;
